import { useState, useEffect, useMemo } from "react";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";

const MANUFACTURING_TERMS = [
  "Welding",
  "Grinding",
  "Cutting",
  "Forming",
  "Forging",
  "Casting",
  "Milling",
  "Turning",
  "Drilling",
  "Tapping",
  "Threading",
  "Stamping",
  "Rolling",
  "Drawing",
  "Extruding",
  "Annealing",
  "Hardening",
  "Tempering",
  "Coating",
  "Plating",
  "Polishing",
  "Buffing",
  "Inspecting",
  "Torquing",
  "Fastening",
  "Riveting",
  "Shearing",
  "Notching",
  "Chamfering",
  "Countersinking",
  "Boring",
  "Reaming",
  "Broaching",
  "Honing",
  "Lapping",
  "Sandblasting",
  "Powder coating",
  "Galvanizing",
  "Spot welding",
  "Packing",
  "Palletizing",
  "Shipping",
  "Compounding",
  "Pressing",
  "Curing",
  "Molding",
  "Injecting",
  "Estimating",
] as const;

/** Unicode braille spinner frame data (sourced from unicode-animations). */
interface Spinner {
  readonly frames: readonly string[];
  readonly interval: number;
}

const SPINNERS: readonly Spinner[] = [
  {
    interval: 80,
    frames: [
      "⢌⣉⢎⣉",
      "⣉⡱⣉⡱",
      "⣉⢎⣉⢎",
      "⡱⣉⡱⣉",
      "⢎⣉⢎⣉",
      "⣉⡱⣉⡱",
      "⣉⢎⣉⢎",
      "⡱⣉⡱⣉",
      "⢎⣉⢎⣉",
      "⣉⡱⣉⡱",
      "⣉⢎⣉⢎",
      "⡱⣉⡱⣉",
      "⢎⣉⢎⣉",
      "⣉⡱⣉⡱",
      "⣉⢎⣉⢎",
      "⡱⣉⡱⣉",
    ],
  }, // helix
  { interval: 100, frames: ["⠃", "⠉", "⠘", "⠰", "⢠", "⣀", "⡄", "⠆"] }, // orbit
  { interval: 80, frames: ["⠋", "⠙", "⠹", "⠸", "⠼", "⠴", "⠦", "⠧", "⠇", "⠏"] }, // braille
  { interval: 180, frames: ["⠀⠶⠀", "⠰⣿⠆", "⢾⣉⡷", "⣏⠀⣹", "⡁⠀⢈"] }, // pulse
  {
    interval: 80,
    frames: [
      "⣁⡀",
      "⣉⠀",
      "⡉⠁",
      "⠉⠉",
      "⠈⠙",
      "⠀⠛",
      "⠐⠚",
      "⠒⠒",
      "⠖⠂",
      "⠶⠀",
      "⠦⠄",
      "⠤⠤",
      "⠠⢤",
      "⠀⣤",
      "⢀⣠",
      "⣀⣀",
    ],
  }, // snake
  { interval: 150, frames: ["⡡⠊⢔⠡", "⠊⡰⡡⡘", "⢔⢅⠈⢢", "⡁⢂⠆⡍", "⢔⠨⢑⢐", "⠨⡑⡠⠊"] }, // sparkle
  {
    interval: 100,
    frames: [
      "⠀",
      "⠂",
      "⠌",
      "⡑",
      "⢕",
      "⢝",
      "⣫",
      "⣟",
      "⣿",
      "⣟",
      "⣫",
      "⢝",
      "⢕",
      "⡑",
      "⠌",
      "⠂",
      "⠀",
    ],
  }, // breathe
  {
    interval: 60,
    frames: [
      "⠀⠀⠀⠀",
      "⠀⠀⠀⠀",
      "⠁⠀⠀⠀",
      "⠋⠀⠀⠀",
      "⠞⠁⠀⠀",
      "⡴⠋⠀⠀",
      "⣠⠞⠁⠀",
      "⢀⡴⠋⠀",
      "⠀⣠⠞⠁",
      "⠀⢀⡴⠋",
      "⠀⠀⣠⠞",
      "⠀⠀⢀⡴",
      "⠀⠀⠀⣠",
      "⠀⠀⠀⢀",
    ],
  }, // cascade
  {
    interval: 80,
    frames: [
      "⠋⠉⠙⠚",
      "⠉⠙⠚⠒",
      "⠙⠚⠒⠂",
      "⠚⠒⠂⠂",
      "⠒⠂⠂⠒",
      "⠂⠂⠒⠲",
      "⠂⠒⠲⠴",
      "⠒⠲⠴⠤",
      "⠲⠴⠤⠄",
      "⠴⠤⠄⠋",
      "⠤⠄⠋⠉",
      "⠄⠋⠉⠙",
    ],
  }, // dna
] as const;

const TERM_CYCLE_MS = 2500;

/**
 * Picks a random index from an array, avoiding the previous index.
 * @param length - The length of the array to pick from
 * @param previousIndex - The index to avoid
 * @returns A random index different from previousIndex
 */
function pickRandomIndex(length: number, previousIndex: number): number {
  if (length <= 1) {
    return 0;
  }
  let next = Math.floor(Math.random() * (length - 1));
  if (next >= previousIndex) {
    next += 1;
  }
  return next;
}

interface ManufacturingSpinnerProps {
  className?: string;
}

/**
 * A fun loading spinner that cycles through manufacturing terms
 * with animated unicode braille spinners.
 * @param className - Optional CSS class name for the container
 * @returns A spinning unicode animation with cycling manufacturing terms
 */
export function ManufacturingSpinner({ className }: ManufacturingSpinnerProps) {
  const [frame, setFrame] = useState(0);
  const [termIndex, setTermIndex] = useState(() =>
    Math.floor(Math.random() * MANUFACTURING_TERMS.length),
  );
  const [spinner] = useState<Spinner>(
    () => SPINNERS[Math.floor(Math.random() * SPINNERS.length)],
  );

  const spinnerWidth = useMemo(
    () => Math.max(...spinner.frames.map((f) => f.length)),
    [spinner],
  );

  useEffect(() => {
    const frameTimer = setInterval(
      () => setFrame((f) => (f + 1) % spinner.frames.length),
      spinner.interval,
    );
    return () => clearInterval(frameTimer);
  }, [spinner]);

  useEffect(() => {
    const termTimer = setInterval(() => {
      setTermIndex((prev) => pickRandomIndex(MANUFACTURING_TERMS.length, prev));
    }, TERM_CYCLE_MS);
    return () => clearInterval(termTimer);
  }, []);

  return (
    <motion.span
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className={cn(
        "inline-flex h-4 items-center gap-1.5 overflow-hidden whitespace-nowrap pr-2 font-mono text-foreground",
        className,
      )}
    >
      <span
        className="text-center leading-none"
        style={{ width: `${spinnerWidth + 1}ch` }}
        aria-hidden="true"
      >
        {spinner.frames[frame]}
      </span>
      <AnimatePresence mode="wait">
        <motion.span
          key={termIndex}
          initial={{ opacity: 0, y: 3 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -3 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
        >
          {MANUFACTURING_TERMS[termIndex]}…
        </motion.span>
      </AnimatePresence>
    </motion.span>
  );
}
